import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, Overline, EmptyState, EmptyValue } from "@/components/portal/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Pencil, Plus, Trash2, Loader2, ExternalLink, ShieldCheck, Lock, AlertTriangle, Link2,
} from "lucide-react";

const SOURCE_FIELDS = [
  ["measurables", "Measurables"],
  ["grade", "Grade / rating"],
  ["rank", "Rank"],
  ["stars", "Stars"],
  ["stats", "Stats"],
  ["honors", "Honors / offers"],
  ["caveats", "Caveats"],
];

function Field({ label, value, empty = "Unverified" }) {
  return (
    <div className="py-2 border-b border-border/50 last:border-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {value ? (
        <div className="text-sm text-white/90 whitespace-pre-wrap mt-0.5">{value}</div>
      ) : (
        <div className="mt-1"><EmptyValue label={empty} /></div>
      )}
    </div>
  );
}

function SourceCard({ s }) {
  return (
    <Panel className="p-5" data-testid={`recruiting-source-${(s.label || "src").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-display text-lg text-white">{s.label}</div>
        {s.url && (
          <a href={s.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#E1FF00] transition-colors">
            Source <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
      {s.retrieved_date && (
        <p className="text-[11px] text-muted-foreground/70 mb-2">Retrieved {s.retrieved_date}</p>
      )}
      <div className="grid sm:grid-cols-2 gap-x-8">
        {SOURCE_FIELDS.map(([k, label]) =>
          s[k] ? <Field key={k} label={label} value={s[k]} /> : null
        )}
      </div>
    </Panel>
  );
}

export function RecruitingTab({ athlete, isAdmin, onSaved }) {
  const r = athlete.recruiting;
  const hasData = !!(r && (r.sources?.length || r.commitment || r.canonical_note || r.hometown || r.caveats));
  const handles = r?.public_handles || {};

  return (
    <div className="space-y-6" data-testid="recruiting-tab">
      <Panel className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <Overline>Recruiting research</Overline>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Private, behind login. Public sports/recruiting data only — no private contact details
              and no PFF. Per-source facts are kept separate and never merged into one ranking;
              anything unavailable stays <span className="text-white/80">Unverified</span>, never zero.
            </p>
            {r?.retrieved_date && (
              <p className="text-[11px] text-muted-foreground/70 mt-2">Checked {r.retrieved_date}</p>
            )}
          </div>
          {isAdmin && <RecruitingEditDialog athlete={athlete} onSaved={onSaved} />}
        </div>
      </Panel>

      {!hasData ? (
        <EmptyState testId="recruiting-empty" title="No recruiting research yet"
          hint={isAdmin ? "Use “Edit research” to add sources, measurables and caveats." : "Research will appear here once added."} />
      ) : (
        <>
          <Panel className="p-6" data-testid="recruiting-canonical">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-4 w-4 text-[#E1FF00]" />
              <Overline>Team-supplied canonical bio{r.canonical_bio_source ? ` · ${r.canonical_bio_source}` : ""}</Overline>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8">
              <Field label="Height (team)" value={athlete.height} />
              <Field label="Weight (team)" value={athlete.weight} />
              <Field label="Position" value={athlete.position} />
              <Field label="Classification" value={athlete.classification} />
              <Field label="Hometown" value={r.hometown} />
              <Field label="Class year" value={r.class_year} />
              <Field label="Grade level" value={r.grade_level} />
              <Field
                label="Commitment"
                value={r.commitment ? `${r.commitment}${r.commitment_status && !r.commitment.toLowerCase().includes(r.commitment_status.toLowerCase()) ? ` · ${r.commitment_status}` : ""}` : null}
                empty="Unverified"
              />
            </div>
            {r.canonical_note && (
              <p className="text-xs text-muted-foreground mt-4 leading-relaxed" data-testid="recruiting-canonical-note">
                {r.canonical_note}
              </p>
            )}
          </Panel>

          {(handles.instagram || handles.x) && (
            <Panel className="p-6" data-testid="recruiting-handles">
              <Overline className="mb-3">Public handles</Overline>
              <p className="text-xs text-muted-foreground/70 mb-3">Public usernames only — no private contact details.</p>
              <div className="flex flex-wrap gap-3">
                {handles.instagram && (
                  <a href={`https://instagram.com/${handles.instagram}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-white hover:text-[#E1FF00] transition-colors">
                    <Link2 className="h-3.5 w-3.5" /> Instagram @{handles.instagram}
                  </a>
                )}
                {handles.x && (
                  <a href={`https://x.com/${handles.x}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-white hover:text-[#E1FF00] transition-colors">
                    <Link2 className="h-3.5 w-3.5" /> X @{handles.x}
                  </a>
                )}
              </div>
            </Panel>
          )}

          {r.caveats && (
            <Panel className="p-6 border-[#E1FF00]/20" data-testid="recruiting-caveats">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-[#E1FF00]" />
                <Overline className="text-[#E1FF00]">Identity & data caveats</Overline>
              </div>
              <p className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap">{r.caveats}</p>
            </Panel>
          )}

          {r.sources?.length > 0 && (
            <div className="space-y-4" data-testid="recruiting-sources">
              <Overline>Sources ({r.sources.length})</Overline>
              <div className="grid lg:grid-cols-2 gap-4">
                {r.sources.map((s, i) => <SourceCard key={i} s={s} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------------- Admin edit dialog ---------------- */
const BLANK_SOURCE = { label: "", url: "", retrieved_date: "", measurables: "", grade: "", rank: "", stars: "", stats: "", honors: "", caveats: "" };

export function RecruitingEditDialog({ athlete, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alias, setAlias] = useState("");
  const [form, setForm] = useState({});
  const [ig, setIg] = useState("");
  const [x, setX] = useState("");
  const [sources, setSources] = useState([{ ...BLANK_SOURCE }]);

  useEffect(() => {
    if (!open) return;
    const r = athlete.recruiting || {};
    setAlias(athlete.alias || "");
    setForm({
      hometown: r.hometown || "", class_year: r.class_year || "", grade_level: r.grade_level || "",
      commitment: r.commitment || "", commitment_status: r.commitment_status || "",
      canonical_bio_source: r.canonical_bio_source || "", canonical_note: r.canonical_note || "",
      caveats: r.caveats || "", retrieved_date: r.retrieved_date || "",
    });
    setIg(r.public_handles?.instagram || "");
    setX(r.public_handles?.x || "");
    setSources(r.sources?.length ? r.sources.map((s) => ({ ...BLANK_SOURCE, ...s })) : [{ ...BLANK_SOURCE }]);
  }, [open, athlete]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setSrc = (i, k) => (e) => setSources((p) => p.map((s, j) => (j === i ? { ...s, [k]: e.target.value } : s)));
  const addSource = () => setSources((p) => [...p, { ...BLANK_SOURCE }]);
  const removeSource = (i) => setSources((p) => p.filter((_, j) => j !== i));

  const nn = (v) => { const t = (v ?? "").trim(); return t === "" ? null : t; };

  const save = async () => {
    setSaving(true);
    try {
      const handles = {};
      if (nn(ig)) handles.instagram = nn(ig).replace(/^@/, "");
      if (nn(x)) handles.x = nn(x).replace(/^@/, "");
      const recruiting = {
        hometown: nn(form.hometown), class_year: nn(form.class_year), grade_level: nn(form.grade_level),
        commitment: nn(form.commitment), commitment_status: nn(form.commitment_status),
        canonical_bio_source: nn(form.canonical_bio_source), canonical_note: nn(form.canonical_note),
        caveats: nn(form.caveats), retrieved_date: nn(form.retrieved_date),
        public_handles: Object.keys(handles).length ? handles : null,
        sources: sources
          .filter((s) => nn(s.label))
          .map((s) => ({
            label: nn(s.label), url: nn(s.url), retrieved_date: nn(s.retrieved_date),
            measurables: nn(s.measurables), grade: nn(s.grade), rank: nn(s.rank), stars: nn(s.stars),
            stats: nn(s.stats), honors: nn(s.honors), caveats: nn(s.caveats),
          })),
      };
      await api.put(`/athletes/${athlete.id}`, { alias: nn(alias), recruiting });
      toast.success("Recruiting research saved");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-border gap-2" data-testid="edit-recruiting-button">
          <Pencil className="h-4 w-4" /> Edit research
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Recruiting research — {athlete.name}</DialogTitle>
          <DialogDescription>
            Private, behind login. Keep per-source facts separate. Leave anything unavailable blank
            (shows as “Unverified”) — do not enter zeros. No private contact details, no PFF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Alias / alternate name</Label>
          <Input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="e.g. Trey Warren III" className="bg-secondary border-border" data-testid="recruiting-alias" />
        </div>

        <Overline className="mt-3">Identity & commitment</Overline>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            ["hometown", "Hometown"], ["class_year", "Class year"], ["grade_level", "Grade level"],
            ["commitment", "Commitment"], ["commitment_status", "Commitment status"], ["retrieved_date", "Retrieved date"],
            ["canonical_bio_source", "Canonical bio source"],
          ].map(([k, l]) => (
            <div key={k} className="space-y-2">
              <Label>{l}</Label>
              <Input value={form[k] ?? ""} onChange={set(k)} className="bg-secondary border-border" data-testid={`recruiting-${k}`} />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Label>Canonical note (team vs recruiting-site measurables)</Label>
          <Textarea value={form.canonical_note ?? ""} onChange={set("canonical_note")} rows={2} className="bg-secondary border-border" data-testid="recruiting-canonical_note" />
        </div>

        <Overline className="mt-3">Public handles (usernames only)</Overline>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Instagram</Label>
            <Input value={ig} onChange={(e) => setIg(e.target.value)} placeholder="username" className="bg-secondary border-border" data-testid="recruiting-ig" /></div>
          <div className="space-y-2"><Label>X</Label>
            <Input value={x} onChange={(e) => setX(e.target.value)} placeholder="username" className="bg-secondary border-border" data-testid="recruiting-x" /></div>
        </div>

        <div className="space-y-2">
          <Label>Identity & data caveats</Label>
          <Textarea value={form.caveats ?? ""} onChange={set("caveats")} rows={3} className="bg-secondary border-border" data-testid="recruiting-caveats-input" />
        </div>

        <div className="flex items-center justify-between mt-3">
          <Overline>Sources</Overline>
          <Button size="sm" variant="outline" className="border-border gap-1" onClick={addSource} data-testid="recruiting-add-source">
            <Plus className="h-3.5 w-3.5" /> Add source
          </Button>
        </div>
        <div className="space-y-4">
          {sources.map((s, i) => (
            <Panel key={i} className="p-4 space-y-3" data-testid={`recruiting-source-row-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={s.label} onChange={setSrc(i, "label")} placeholder="Source label (e.g. 247Sports)" className="bg-secondary border-border" data-testid={`recruiting-source-label-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => removeSource(i)} data-testid={`recruiting-remove-source-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={s.url} onChange={setSrc(i, "url")} placeholder="Source URL" className="bg-secondary border-border" />
                <Input value={s.retrieved_date} onChange={setSrc(i, "retrieved_date")} placeholder="Retrieved date" className="bg-secondary border-border" />
                <Input value={s.measurables} onChange={setSrc(i, "measurables")} placeholder="Measurables" className="bg-secondary border-border" />
                <Input value={s.grade} onChange={setSrc(i, "grade")} placeholder="Grade / rating" className="bg-secondary border-border" />
                <Input value={s.rank} onChange={setSrc(i, "rank")} placeholder="Rank" className="bg-secondary border-border" />
                <Input value={s.stars} onChange={setSrc(i, "stars")} placeholder="Stars" className="bg-secondary border-border" />
              </div>
              <Textarea value={s.stats} onChange={setSrc(i, "stats")} rows={2} placeholder="Stats" className="bg-secondary border-border" />
              <Textarea value={s.honors} onChange={setSrc(i, "honors")} rows={2} placeholder="Honors / offers" className="bg-secondary border-border" />
              <Textarea value={s.caveats} onChange={setSrc(i, "caveats")} rows={2} placeholder="Caveats" className="bg-secondary border-border" />
            </Panel>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-white text-black hover:bg-white/90 font-semibold" data-testid="recruiting-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save research"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
